import { useEffect, useMemo, useState } from 'react';

// A keyboard-driven viewport shrink is always larger than this; a smaller
// change is treated as "keyboard closed".
export const KEYBOARD_OPEN_MIN_SHRINK_PX = 150;

type UseKeepMaximizedOnKeyboardHideParams = {
  minSnap: number;
  maxSnap: number;
  /** Current visual viewport height (shrinks while the keyboard is open). */
  viewportHeight: number;
  /** Largest viewport height seen, i.e. the height with no keyboard open. */
  baseViewportHeight: number;
};

type UseKeepMaximizedOnKeyboardHide = {
  /**
   * Snap points for the bottom sheet. While locked, the min snap is dropped,
   * leaving `maxSnap` as the only target.
   */
  snapPoints: number[];
  /**
   * Lock the sheet at maxSnap. Call on pointer-down of an in-drawer control
   * that steals focus from the search input.
   */
  lockSheetOnKeyboardDismiss: () => void;
  /** Unlock so the sheet can reach its min snap again. */
  unlockSheet: () => void;
};

/**
 * Keeps the search bottom sheet from collapsing when the on-screen keyboard
 * hides.
 *
 * Tapping a control inside the drawer (filter, sort, historical toggle...)
 * blurs the search input and hides the keyboard, which grows the viewport and
 * therefore `maxSnap`. react-spring-bottom-sheet reacts to that growth by
 * re-snapping to the point nearest the current (small, keyboard-height)
 * position (the minimum) collapsing the drawer.
 *
 * Locking drops the min snap point, so `maxSnap` is the only option and the
 * sheet grows to fill the reclaimed space instead of snapping shut. The lock
 * is released automatically once the keyboard has hidden. Dragging the sheet
 * down never locks it, so drag-to-dismiss still works.
 */
export const useKeepMaximizedOnKeyboardHide = ({
  minSnap,
  maxSnap,
  viewportHeight,
  baseViewportHeight,
}: UseKeepMaximizedOnKeyboardHideParams): UseKeepMaximizedOnKeyboardHide => {
  const [locked, setKeepMaximized] = useState(false);

  const keyboardOpen =
    baseViewportHeight - viewportHeight > KEYBOARD_OPEN_MIN_SHRINK_PX;

  const snapPoints = useMemo(
    () => (locked ? [maxSnap] : [minSnap, maxSnap]),
    [locked, minSnap, maxSnap],
  );

  // Release once the keyboard has hidden so the user can collapse the sheet again.
  useEffect(() => {
    if (locked && !keyboardOpen) {
      setKeepMaximized(false);
    }
  }, [locked, keyboardOpen]);

  return {
    snapPoints,
    lockSheetOnKeyboardDismiss: () => {
      if (keyboardOpen) setKeepMaximized(true);
    },
    unlockSheet: () => setKeepMaximized(false),
  };
};
